import {constructUsing, createMap, forMember, mapFrom, type Mapper} from '@automapper/core';
import type {IMapping} from '@igp/shared/auto-mapper';
import type {CreateRepaymentLogInput, EditRepaymentLogInput, RepaymentLog} from '@igp/shared/contract/pam';
import {formatTimestamp} from '@igp/shared/utils';

import type {
    CreateRepaymentLogPayload,
    EditRepaymentLogPayload,
    RepaymentLogColumns,
    RepaymentLogDownloadFields,
    RepaymentLogDownloadModel,
    RepaymentLogQueryFields,
    RepaymentLogSortKeys,
    RepaymentLogViewModel,
    RepaymentLogViewModelKeys,
} from './types';

export const repaymentLogMappingKeys = {
    model: 'RepaymentLog',
    viewModel: 'RepaymentLogViewModel',
    downloadModel: 'RepaymentLogDownloadModel',
    viewModelsKeys: 'RepaymentLogViewModelKeys',
    queryFileds: 'RepaymentLogQueryFields',
    downloadFields: 'RepaymentLogDownloadFields',
    sortKeys: 'RepaymentLogSortKeys',
    createPayload: 'CreateRepaymentLogPayload',
    editPayload: 'EditRepaymentLogPayload',
    createInput: 'CreateRepaymentLogInput',
    editInput: 'EditRepaymentLogInput',
    columns: 'RepaymentLogColumns',
} as const;

export class RepaymentLogMapper implements IMapping {
    createMapping(mapper: Mapper): void {
        createMap<RepaymentLog, RepaymentLogViewModel>(
            mapper,
            repaymentLogMappingKeys.model,
            repaymentLogMappingKeys.viewModel,
            forMember(
                dm => dm.repaymentId,
                mapFrom(m => m.repayment_id)
            ),
            forMember(
                dm => dm.repaymentAmount,
                mapFrom(m => m.repayment_amount)
            ),
            forMember(
                dm => dm.repaymentDate,
                mapFrom(m => m.repayment_date)
            ),
            forMember(
                dm => dm.masterAgentUid,
                mapFrom(m => m.master_agent_uid)
            ),
            forMember(
                dm => dm.masterAgentUsername,
                mapFrom(m => m.master_agent_username)
            ),
            forMember(
                dm => dm.fromAgentUid,
                mapFrom(m => m.from_agent_uid)
            ),
            forMember(
                dm => dm.fromAgentUsername,
                mapFrom(m => m.from_agent_username)
            )
        );
        createMap<RepaymentLogViewModelKeys, RepaymentLogQueryFields>(
            mapper,
            repaymentLogMappingKeys.viewModelsKeys,
            repaymentLogMappingKeys.queryFileds,
            constructUsing(key => {
                const mapping: Record<RepaymentLogViewModelKeys, RepaymentLogQueryFields> = {
                    repaymentId: 'repayment_id',
                    repaymentAmount: 'repayment_amount',
                    repaymentDate: 'repayment_date.seconds',
                    masterAgentUid: 'master_agent_uid',
                    masterAgentUsername: 'master_agent_username',
                    fromAgentUid: 'from_agent_uid',
                    fromAgentUsername: 'from_agent_username',
                };
                return mapping[key];
            })
        );
        createMap<RepaymentLogViewModelKeys, RepaymentLogDownloadFields>(
            mapper,
            repaymentLogMappingKeys.viewModelsKeys,
            repaymentLogMappingKeys.downloadFields,
            constructUsing(key => {
                const mapping: Record<RepaymentLogViewModelKeys, RepaymentLogDownloadFields> = {
                    repaymentId: 'repayment_id',
                    repaymentAmount: 'repayment_amount',
                    repaymentDate: 'repayment_date.seconds',
                    masterAgentUid: 'master_agent_uid',
                    masterAgentUsername: 'master_agent_username',
                    fromAgentUid: 'from_agent_uid',
                    fromAgentUsername: 'from_agent_username',
                };
                return mapping[key];
            })
        );
        createMap<RepaymentLogViewModelKeys, RepaymentLogSortKeys>(
            mapper,
            repaymentLogMappingKeys.viewModelsKeys,
            repaymentLogMappingKeys.sortKeys,
            constructUsing(key => {
                const mapping: Record<RepaymentLogViewModelKeys, RepaymentLogSortKeys> = {
                    repaymentId: 'repayment_id',
                    repaymentAmount: 'repayment_amount',
                    repaymentDate: 'repayment_date.seconds',
                    masterAgentUid: 'master_agent_uid',
                    masterAgentUsername: 'master_agent_username',
                    fromAgentUid: 'from_agent_uid',
                    fromAgentUsername: 'from_agent_username',
                };
                return mapping[key];
            })
        );
        createMap<RepaymentLogSortKeys, RepaymentLogViewModelKeys>(
            mapper,
            repaymentLogMappingKeys.sortKeys,
            repaymentLogMappingKeys.viewModelsKeys,
            constructUsing(key => {
                const mapping: Record<RepaymentLogSortKeys, RepaymentLogViewModelKeys> = {
                    repayment_id: 'repaymentId',
                    repayment_amount: 'repaymentAmount',
                    'repayment_date.seconds': 'repaymentDate',
                    'repayment_date.nanos': 'repaymentDate',
                    master_agent_uid: 'masterAgentUid',
                    master_agent_username: 'masterAgentUsername',
                    from_agent_uid: 'fromAgentUid',
                    from_agent_username: 'fromAgentUsername',
                };
                return mapping[key];
            })
        );
        createMap<RepaymentLog, RepaymentLogDownloadModel>(
            mapper,
            repaymentLogMappingKeys.model,
            repaymentLogMappingKeys.downloadModel,
            forMember(
                dm => dm.repaymentId,
                mapFrom(m => m.repayment_id)
            ),
            forMember(
                dm => dm.repaymentAmount,
                mapFrom(m => m.repayment_amount?.toFixed(2))
            ),
            forMember(
                dm => dm.repaymentDate,
                mapFrom(m => (m.repayment_date ? formatTimestamp(m.repayment_date, 'date', false) : undefined))
            ),
            forMember(
                dm => dm.masterAgentUid,
                mapFrom(m => m.master_agent_uid)
            ),
            forMember(
                dm => dm.masterAgentUsername,
                mapFrom(m => m.master_agent_username)
            ),
            forMember(
                dm => dm.fromAgentUid,
                mapFrom(m => m.from_agent_uid)
            ),
            forMember(
                dm => dm.fromAgentUsername,
                mapFrom(m => m.from_agent_username)
            )
        );
        createMap<CreateRepaymentLogPayload, CreateRepaymentLogInput>(
            mapper,
            repaymentLogMappingKeys.createPayload,
            repaymentLogMappingKeys.createInput,
            forMember(
                input => input.repayment_date,
                mapFrom(payload => payload.repaymentDate)
            ),
            forMember(
                input => input.repayment_amount,
                mapFrom(payload => payload.repaymentAmount)
            ),
            forMember(
                input => input.master_agent_uid,
                mapFrom(payload => payload.masterAgentUid)
            ),
            forMember(
                input => input.from_agent_uid,
                mapFrom(payload => payload.fromAgentUid)
            )
        );
        createMap<EditRepaymentLogPayload, EditRepaymentLogInput>(
            mapper,
            repaymentLogMappingKeys.editPayload,
            repaymentLogMappingKeys.editInput,
            forMember(
                input => input.repayment_id,
                mapFrom(payload => payload.repaymentId)
            ),
            forMember(
                input => input.repayment_date,
                mapFrom(payload => ({
                    seconds: payload.repaymentDate?.seconds,
                    nanos: payload.repaymentDate?.nanos,
                }))
            ),
            forMember(
                input => input.repayment_amount,
                mapFrom(payload => payload.repaymentAmount)
            ),
            forMember(
                input => input.master_agent_uid,
                mapFrom(payload => payload.masterAgentUid)
            ),
            forMember(
                input => input.from_agent_uid,
                mapFrom(payload => payload.fromAgentUid)
            )
        );
        createMap<RepaymentLogColumns, RepaymentLogViewModelKeys[]>(
            mapper,
            repaymentLogMappingKeys.columns,
            repaymentLogMappingKeys.viewModelsKeys,
            constructUsing(column => {
                const mapping: Record<RepaymentLogColumns, RepaymentLogViewModelKeys[]> = {
                    repaymentAmount: ['repaymentAmount'],
                    repaymentDate: ['repaymentDate'],
                    remove: ['fromAgentUid', 'masterAgentUid', 'repaymentDate', 'repaymentAmount', 'repaymentId'],
                    edit: ['fromAgentUid', 'masterAgentUid', 'repaymentDate', 'repaymentAmount', 'repaymentId'],
                    fromAgentUid: ['fromAgentUid'],
                    fromAgentUsername: ['fromAgentUsername'],
                    masterAgentUid: ['masterAgentUid'],
                    masterAgentUsername: ['masterAgentUsername'],
                };
                return mapping[column];
            })
        );
    }
}
